import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  AlertTriangle,
  BarChart3,
  Briefcase,
  CreditCard,
  Home,
  LogOut,
  Menu,
  Ticket,
  Package,
  Settings,
  Users,
  DollarSign,
  UserCircle,
  Wallet,
  X,
  ShoppingCart,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { ClientModal } from '@/components/clients/ClientModal';
import { NotificationBadge } from '@/components/notifications/NotificationBadge';
import { OrderCreateModal } from '@/components/orders/OrderCreateModal';
import { SettingsModal } from '@/components/settings/SettingsModal';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Logo } from '@/components/ui/Logo';
import { useAuth } from '@/hooks/useAuth';
import { billingService } from '@/services/billing';
import { professionalProfileService } from '@/services/marketplace/professionalProfile';
import { userService } from '@/services/users';
import { useAuthStore } from '@/stores/authStore';
import type { Order } from '@/types/api';
import { hasLegacyKanbanAccess } from '@/utils/legacyAccess';
import { APP_VERSION } from '@/utils/version';

const ONBOARDING_BANNER_DISMISSED_KEY = 'pedidoo.professionalOnboardingBannerDismissed';

export interface AppLayoutContext {
  openOrderModal: () => void;
  openEditOrderModal: (order: Order) => void;
}

const appBasePath = '/app';

const navItems = [
  { to: appBasePath, label: 'Home', icon: Home, roles: ['SUPERADMIN', 'ADMIN', 'ADMIN_CLI', 'USER'] },
  { to: `${appBasePath}/orders`, label: 'Pedidos', icon: ShoppingCart, roles: ['SUPERADMIN', 'ADMIN', 'ADMIN_CLI', 'USER'] },
  { to: `${appBasePath}/clients`, label: 'Clientes', icon: Users, roles: ['SUPERADMIN', 'ADMIN', 'ADMIN_CLI', 'USER'] },
  { to: `${appBasePath}/products`, label: 'Produtos', icon: Package, roles: ['SUPERADMIN', 'ADMIN', 'ADMIN_CLI', 'USER'] },
  { to: `${appBasePath}/reports`, label: 'Relatórios', icon: BarChart3, roles: ['SUPERADMIN', 'ADMIN', 'ADMIN_CLI', 'USER'] },
  { to: `${appBasePath}/leads`, label: 'Leads', icon: Briefcase, roles: ['SUPERADMIN', 'ADMIN', 'ADMIN_CLI', 'USER'] },
  { to: `${appBasePath}/perfil-profissional`, label: 'Meu perfil profissional', icon: UserCircle, roles: ['SUPERADMIN', 'ADMIN', 'ADMIN_CLI', 'USER'] },
  { to: `${appBasePath}/marketplace-pagamentos`, label: 'Pagamentos de Especialistas', icon: Wallet, roles: ['SUPERADMIN', 'ADMIN'] },
  { to: `${appBasePath}/billing`, label: 'Meu plano', icon: CreditCard, roles: ['ADMIN', 'ADMIN_CLI', 'USER'] }, // Removido SUPERADMIN
  { to: `${appBasePath}/users`, label: 'Usuários', icon: Users, roles: ['SUPERADMIN', 'ADMIN'] }, // Apenas SUPERADMIN e ADMIN
  { to: `${appBasePath}/subscriptions`, label: 'Mensalidades', icon: DollarSign, roles: ['SUPERADMIN', 'ADMIN'] }, // Apenas SUPERADMIN e ADMIN
  { to: `${appBasePath}/settings`, label: 'Configurações', icon: Settings, roles: ['SUPERADMIN', 'ADMIN'] }, // Apenas SUPERADMIN e ADMIN
  { to: `${appBasePath}/rifas`, label: 'Rifas', icon: Ticket, roles: ['SUPERADMIN', 'ADMIN'] },
];

export const AppLayout = () => {
  const user = useAuthStore((state) => state.user);
  const setAuth = useAuthStore((state) => state.setAuth);
  const accessToken = useAuthStore((state) => state.accessToken);
  const remember = useAuthStore((state) => state.remember);
  const { logout } = useAuth();
  const [isOrderModalOpen, setOrderModalOpen] = useState(false);
  const [isClientModalOpen, setClientModalOpen] = useState(false);
  const [orderToEdit, setOrderToEdit] = useState<Order | null>(null);
  const [isSettingsModalOpen, setSettingsModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState<boolean>(true);
  const isDemo = user?.email === 'demo@pedidoo.com';
  const navigate = useNavigate();
  const location = useLocation();

  const queryClient = useQueryClient();
  const billingSummaryQuery = useQuery({
    queryKey: ['billing', 'summary'],
    queryFn: billingService.getSummary,
    enabled: Boolean(user),
    staleTime: 60_000,
    retry: 1,
    retryDelay: 1000,
  });
  const accessFromSummary = billingSummaryQuery.data?.accessEnabled;
  const hasTemporarySuspension = Boolean(
    billingSummaryQuery.data?.subscription?.temporarilyInactive,
  );

  useEffect(() => {
    if (!user || accessFromSummary === undefined) {
      return;
    }

    const currentAccess = user.accessEnabled ?? true;
    if (currentAccess !== accessFromSummary) {
      setAuth(
        {
          user: { ...user, accessEnabled: accessFromSummary },
          accessToken: accessToken ?? '',
        },
        remember,
      );
    }
  }, [accessFromSummary, user, setAuth, accessToken, remember]);

  const isProfessionalRole = user?.role === 'ADMIN' || user?.role === 'ADMIN_CLI';
  const isOnboardingCandidate = isProfessionalRole && !hasLegacyKanbanAccess(user);

  const professionalProfileQuery = useQuery({
    queryKey: ['marketplace', 'professional', 'profile'],
    queryFn: professionalProfileService.getMine,
    enabled: isOnboardingCandidate,
    staleTime: 60_000,
  });

  const [isOnboardingBannerDismissed, setIsOnboardingBannerDismissed] = useState(
    () => typeof window !== 'undefined' && sessionStorage.getItem(ONBOARDING_BANNER_DISMISSED_KEY) === 'true',
  );

  const showOnboardingBanner =
    isOnboardingCandidate &&
    professionalProfileQuery.isSuccess &&
    !professionalProfileQuery.data?.profileComplete &&
    !isOnboardingBannerDismissed;

  const dismissOnboardingBanner = () => {
    sessionStorage.setItem(ONBOARDING_BANNER_DISMISSED_KEY, 'true');
    setIsOnboardingBannerDismissed(true);
  };

  // Só considerar acesso bloqueado se a query terminou de carregar (sucesso ou erro)
  const isAccessBlocked = Boolean(
    user &&
      billingSummaryQuery.isSuccess && // Só verificar se a query terminou com sucesso
      (user.role === 'ADMIN_CLI' || user.role === 'USER') &&
      (hasTemporarySuspension || accessFromSummary === false || user.accessEnabled === false),
  );

  useEffect(() => {
    if (isAccessBlocked && !location.pathname.startsWith('/app/billing')) {
      navigate('/app/billing', { replace: true });
    }
  }, [isAccessBlocked, location.pathname, navigate]);

  // Carregar preferências do usuário (notificações + dark mode)
  useEffect(() => {
    if (user) {
      userService
        .getShowNotifications()
        .then((data) => setShowNotifications(data.showNotifications))
        .catch(() => setShowNotifications(true));

      userService
        .getDarkMode()
        .then((data) => {
          if (data.darkMode) {
            document.documentElement.classList.add('dark');
            localStorage.setItem('pedidoo:darkMode', 'true');
          } else {
            document.documentElement.classList.remove('dark');
            localStorage.setItem('pedidoo:darkMode', 'false');
          }
        })
        .catch(() => {
          // Mantém preferência local em caso de falha da API
        });
    }
  }, [user]);

  // Ouvir evento de atualização da preferência
  useEffect(() => {
    const handlePreferenceUpdate = () => {
      if (user) {
        userService
          .getShowNotifications()
          .then((data) => {
            setShowNotifications(data.showNotifications);
          })
          .catch(() => {
            setShowNotifications(true);
          });
      }
    };
    window.addEventListener('notifications-preference-updated', handlePreferenceUpdate);
    return () => {
      window.removeEventListener('notifications-preference-updated', handlePreferenceUpdate);
    };
  }, [user]);

  const shouldWarnOrders = useMemo(() => {
    if (!billingSummaryQuery.data) return null;
    const { usage, limits } = billingSummaryQuery.data;
    if (!limits.ordersPerMonth) return null;
    const ratio = usage.ordersThisMonth / limits.ordersPerMonth;
    if (ratio >= 0.8) {
      return {
        ratio,
        usage: usage.ordersThisMonth,
        limit: limits.ordersPerMonth,
      };
    }
    return null;
  }, [billingSummaryQuery.data]);

  const dateLabel = useMemo(() => format(new Date(), "EEEE, dd 'de' MMMM", { locale: ptBR }), []);

  const openOrderModal = () => {
    setOrderToEdit(null);
    setOrderModalOpen(true);
  };

  const openEditOrderModal = (order: Order) => {
    setOrderToEdit(order);
    setOrderModalOpen(true);
  };

  const closeOrderModal = () => {
    setOrderModalOpen(false);
    setOrderToEdit(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Sessão encerrada');
    } catch {
      toast.error('Erro ao sair');
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-100">
      {/* Menu Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 z-40 bg-slate-900/50 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
      
      {/* Menu Lateral */}
      <aside className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col overflow-y-auto border-r border-slate-200 bg-white p-6 transition-transform duration-300 ease-in-out md:static ${
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
      } dark:border-slate-700 dark:bg-slate-800`}>
        <div className="mb-10 flex items-center justify-between">
          <div>
            <Logo size="md" showText={false} className="cursor-pointer" onClick={() => {
              navigate(appBasePath);
              setIsMobileMenuOpen(false);
            }} />
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Gestão de pedidos simplificada</p>
          </div>
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 md:hidden"
            aria-label="Fechar menu"
          >
            <X className="size-5" />
          </button>
        </div>
        <nav className="space-y-1">
          {(isAccessBlocked
            ? navItems.filter((item) => ['/app/billing', '/app/settings'].includes(item.to))
            : navItems
          )
            .filter((item) => {
              // Filtrar menus baseado no role do usuário
              if (!user) return false;
              if (!item.roles) return true; // Se não especificado, mostrar para todos
              return item.roles.includes(user.role as any);
            })
            .filter((item) => {
              const legacyPaths = [appBasePath, `${appBasePath}/orders`];
              if (!legacyPaths.includes(item.to)) return true;
              return hasLegacyKanbanAccess(user);
            })
            .map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                onClick={() => setIsMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition ${
                    isActive ? 'bg-primary-50 text-primary-600' : 'text-slate-600 hover:bg-slate-100'
                  }`
                }
              >
                <Icon className="size-4" />
                {label}
              </NavLink>
            ))}
        </nav>
        
        {/* Menu de usuário - logo após o menu do sistema */}
        <div className="mt-6 space-y-4">
          <div className="flex items-center gap-3 rounded-lg bg-slate-50 p-3">
            <div className="flex-1">
              <p className="text-sm font-semibold text-slate-700">{user?.name}</p>
              <p className="text-xs text-slate-500">{user?.email}</p>
              {user?.plan && (
                <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-primary-600">
                  Plano {user.plan}
                </p>
              )}
            </div>
            <button
              onClick={() => setSettingsModalOpen(true)}
              className="rounded-lg p-2 text-slate-600 transition-colors hover:bg-slate-200"
              title="Configurações"
            >
              <Settings className="size-4" />
            </button>
          </div>
          <Button variant="outline" className="w-full gap-2" onClick={handleLogout}>
            <LogOut className="size-4" /> Sair
          </Button>
        </div>
        
        {/* Versão no final */}
        <div className="mt-auto pt-4">
          <p className="text-center text-[10px] text-slate-400">
            v{APP_VERSION}
          </p>
        </div>
      </aside>

      <main className="flex flex-1 flex-col">
        <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/80 backdrop-blur">
          <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:px-8">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 md:hidden"
                aria-label="Abrir menu"
              >
                <Menu className="size-6" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-slate-800">
                  {isAccessBlocked ? 'Acesso suspenso' : 'Painel de pedidos'}
                </h1>
                <p className="text-sm text-slate-500">{dateLabel}</p>
              </div>
            </div>
            {!isAccessBlocked && !isDemo && (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
                <div className="flex items-center gap-2">
                  {showNotifications && <NotificationBadge />}
                  <Button variant="secondary" onClick={() => setClientModalOpen(true)}>
                    Novo cliente
                  </Button>
                  <Button onClick={openOrderModal}>Novo pedido</Button>
                </div>
              </div>
            )}
          </div>
          {shouldWarnOrders && (
            <div className="border-t border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-700 sm:px-8">
              Você já utilizou {shouldWarnOrders.usage} de {shouldWarnOrders.limit} pedidos deste
              mês. Considere fazer upgrade do plano para evitar bloqueios.
            </div>
          )}
        </header>

        {showOnboardingBanner && (
          <div className="flex items-center justify-between gap-3 border-b border-primary-200 bg-primary-50 px-4 py-2.5 text-sm text-primary-800 sm:px-8">
            <span>
              Complete seu perfil profissional para começar a receber pedidos de orçamento.{' '}
              <button
                type="button"
                onClick={() => navigate('/app/perfil-profissional')}
                className="font-medium underline underline-offset-2 hover:text-primary-900"
              >
                Completar agora →
              </button>
            </span>
            <button
              type="button"
              onClick={dismissOnboardingBanner}
              className="shrink-0 rounded p-1 text-primary-700 hover:bg-primary-100"
              aria-label="Dispensar aviso"
            >
              <X className="size-4" />
            </button>
          </div>
        )}

        {isDemo && (
          <div className="border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800 sm:px-8">
            <span className="font-medium">Modo demo</span> — dados fictícios para exploração.{' '}
            <a href="/?register=1" className="underline underline-offset-2 hover:text-amber-900">
              Criar conta grátis →
            </a>
          </div>
        )}
        <div className="flex-1 px-4 py-6 sm:px-8">
          {isAccessBlocked ? (
            <Card className="border border-rose-200 bg-rose-50 p-6 text-rose-900">
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="size-5" />
                  <h2 className="text-lg font-semibold">Plano em atraso</h2>
                </div>
                <p className="text-sm">
                  Seu painel foi bloqueado temporariamente. Regularize o pagamento e depois reative o acesso
                  em &quot;Meu plano&quot; para continuar utilizando todas as funcionalidades.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button onClick={() => navigate('/app/billing')} variant="primary">
                    Ir para Meu plano
                  </Button>
                  <Button variant="outline" onClick={handleLogout}>
                    Sair
                  </Button>
                </div>
              </div>
            </Card>
          ) : (
            <Outlet context={{ openOrderModal, openEditOrderModal }} />
          )}
        </div>
      </main>

      <OrderCreateModal
        isOpen={isOrderModalOpen}
        order={orderToEdit ?? undefined}
        onClose={closeOrderModal}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ['orders'] });
        }}
      />
      <ClientModal
        isOpen={isClientModalOpen}
        onClose={() => setClientModalOpen(false)}
        onSaved={(newClient) => {
          queryClient.invalidateQueries({ queryKey: ['clients'] });
          queryClient.invalidateQueries({ queryKey: ['clients', 'options'] });
          toast.success(`Cliente ${newClient.name} cadastrado!`);
        }}
      />
      <SettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        hasPendingCancellation={Boolean(
          billingSummaryQuery.data?.subscription?.cancellationRequested,
        )}
        onCancellationRequest={() => {
          void billingSummaryQuery.refetch();
        }}
      />
    </div>
  );
};
